import { useMemo, useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import PageHeader from "@/components/common/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import EmptyState from "@/components/common/EmptyState";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  GraduationCap,
  ChevronLeft,
  ChevronRight,
  Loader2
} from "lucide-react";
import api from "@/lib/api";

const PAGE_SIZE = 5;

const blank = {
  institution: "",
  degree: "",
  field: "",
  startDate: "",
  endDate: "",
  grade: "",
  description: ""
};

export default function Education() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchItems = async () => {
    try {
      const res = await api.get('/Education');
      if (res.success && res.data) {
        const mapped = res.data.map(d => ({
          id: d.id,
          institution: d.institutionName || "",
          degree: d.degree || "",
          field: d.fieldOfStudy || "",
          startDate: d.startDate ? d.startDate.split("T")[0].substring(0, 7) : "",
          endDate: d.endDate ? d.endDate.split("T")[0].substring(0, 7) : "",
          grade: d.grade || "",
          description: d.description || "",
          displayOrder: d.displayOrder || 0
        }));
        setItems(mapped.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)));
      }
    } catch (err) {
      toast.error("Failed to load Education records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const filtered = useMemo(() => {
    if (!query) return items;
    const q = query.toLowerCase();
    return items.filter(
      (e) =>
        e.institution.toLowerCase().includes(q) ||
        e.degree.toLowerCase().includes(q) ||
        e.field.toLowerCase().includes(q)
    );
  }, [items, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCreate = () => {
    setEditing(null);
    setForm(blank);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.institution || !form.degree) {
      toast.error("Institution and Degree are required.");
      return;
    }
    
    const payload = {
      institutionName: form.institution,
      degree: form.degree,
      fieldOfStudy: form.field,
      startDate: form.startDate ? `${form.startDate}-01` : null,
      endDate: form.endDate ? `${form.endDate}-01` : null,
      grade: form.grade,
      description: form.description || "",
      displayOrder: form.displayOrder || 0
    };

    try {
      if (editing) {
        const res = await api.put(`/Education/${editing.id}`, payload);
        if (res.success) {
          toast.success("Education record updated.");
          fetchItems();
        }
      } else {
        const res = await api.post('/Education', payload);
        if (res.success) {
          toast.success("Education record added.");
          fetchItems();
        }
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to save record.");
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await api.delete(`/Education/${toDelete.id}`);
      if (res.success) {
        toast.success("Education record removed.");
        fetchItems();
      }
    } catch (err) {
      toast.error("Failed to delete record.");
    } finally {
      setConfirmOpen(false);
      setToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Education"
        description="Academic history shown on your portfolio's about page and CV."
        testId="education"
        action={openCreate}
        actionLabel="Add Education"
        actionIcon={Plus}
      />

      <Card className="p-0 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-border">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <Input
              data-testid="education-search"
              placeholder="Search institutions, degrees…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              className="pl-9 h-9"
            />
          </div>
          <Badge variant="secondary" className="font-mono text-[10px] w-fit">
            {filtered.length} {filtered.length === 1 ? "record" : "records"}
          </Badge>
        </div>

        {filtered.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={GraduationCap}
              title="No education records"
              description="Add your first academic credential to populate your portfolio."
              actionLabel="Add Education"
              onAction={openCreate}
              testId="education-empty"
            />
          </div>
        ) : (
          <>
            <Card className="overflow-hidden border-border/50 bg-card">
              <div className="overflow-x-auto w-full">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Institution</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Degree</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Field</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Start</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">End</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Grade</TableHead>
                      <TableHead className="text-right text-[10px] font-semibold uppercase tracking-wider">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginated.map((row) => (
                      <TableRow key={row.id} data-testid={`education-row-${row.id}`} className="hover:bg-muted/40">
                        <TableCell className="font-medium">{row.institution}</TableCell>
                        <TableCell>{row.degree}</TableCell>
                        <TableCell className="text-muted-foreground">{row.field}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{row.startDate}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{row.endDate}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="font-mono text-[10px]">{row.grade}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => openEdit(row)}
                              data-testid={`education-edit-${row.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => {
                                setToDelete(row);
                                setConfirmOpen(true);
                              }}
                              data-testid={`education-delete-${row.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>

            <div className="flex items-center justify-between p-4 border-t border-border">
              <span className="text-xs text-muted-foreground font-mono">
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  data-testid="education-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  data-testid="education-next-page"
                >
                  <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg" data-testid="education-modal">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit education" : "Add education"}
            </DialogTitle>
            <DialogDescription>
              Capture an academic milestone for your portfolio.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Institution" span={2}>
              <Input
                data-testid="education-institution"
                value={form.institution}
                onChange={(e) => setForm({ ...form, institution: e.target.value })}
                placeholder="Stanford University"
              />
            </Field>
            <Field label="Degree">
              <Input
                data-testid="education-degree"
                value={form.degree}
                onChange={(e) => setForm({ ...form, degree: e.target.value })}
                placeholder="M.S."
              />
            </Field>
            <Field label="Field of Study">
              <Input
                data-testid="education-field"
                value={form.field}
                onChange={(e) => setForm({ ...form, field: e.target.value })}
                placeholder="Computer Science"
              />
            </Field>
            <Field label="Start Date">
              <Input
                type="month"
                data-testid="education-start"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </Field>
            <Field label="End Date">
              <Input
                type="month"
                data-testid="education-end"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </Field>
            <Field label="Grade" span={2}>
              <Input
                data-testid="education-grade"
                value={form.grade}
                onChange={(e) => setForm({ ...form, grade: e.target.value })}
                placeholder="3.9 GPA"
              />
            </Field>
            <Field label="Description" span={2}>
              <Textarea
                data-testid="education-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Graduated with Honors..."
                rows={3}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="education-save">
              {editing ? "Save changes" : "Add education"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete education record?"
        description="This will remove the record from your portfolio. This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
}

const Field = ({ label, span = 1, children }) => (
  <div className={span === 2 ? "col-span-2" : "col-span-2 sm:col-span-1"}>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
